import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle, XCircle, CreditCard, Building2, ArrowRight } from 'lucide-react';
import { supabase } from '../supabase';
import { logAdminAction } from '../utils/auditLog';

export default function StripeReconcile() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stripeData, setStripeData] = useState(null);
  const [dbCompanies, setDbCompanies] = useState([]);
  const [lastFetched, setLastFetched] = useState(null);

  const SERVICE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndyZ21pY2Z0ZHlrZ2F5Z2Jua3FuIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc2NTYwMjM1NiwiZXhwIjoyMDgxMTc4MzU2fQ.ssiKFe5dGjiP9QAjFmVeyVrkHCFKz_AznT_85Qsvqxg';

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch Stripe data from API
      const stripeRes = await fetch('https://workvanapp.com/api/stripe-reconcile', {
        headers: { 'Authorization': `Bearer ${SERVICE_KEY}` }
      });
      if (!stripeRes.ok) {
        const errData = await stripeRes.json().catch(() => ({}));
        throw new Error(errData.error || `Stripe API returned ${stripeRes.status}`);
      }
      const stripe = await stripeRes.json();

      // Fetch all companies from DB
      const { data: companies, error: dbErr } = await supabase
        .from('companies')
        .select('id, name, tier, subscription_status, stripe_customer_id, billing_interval, owner_user_id, created_at, trial_ends_at');

      if (dbErr) throw new Error('DB error: ' + dbErr.message);

      setStripeData(stripe);
      setDbCompanies(companies || []);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const [syncing, setSyncing] = useState({});

  const getAdminEmail = () => {
    try {
      return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email || 'unknown';
    } catch { return 'unknown'; }
  };

  const getTargetStatus = (mismatch) => {
    if (mismatch.type === 'status_mismatch') return mismatch.stripeSub.status;
    if (mismatch.type === 'db_has_stripe_id_no_sub') return 'canceled';
    return 'canceled';
  };

  const syncOne = async (mismatch) => {
    const newStatus = getTargetStatus(mismatch);
    const confirmed = window.confirm(
      `Sync "${mismatch.company.name}"?\n\nCurrent DB status: ${mismatch.company.subscription_status || 'null'}\nNew status: ${newStatus}`
    );
    if (!confirmed) return;

    const key = mismatch.company.id;
    setSyncing(prev => ({ ...prev, [key]: true }));
    try {
      const { error: updateErr } = await supabase
        .from('companies')
        .update({ subscription_status: newStatus })
        .eq('id', mismatch.company.id);

      if (updateErr) throw updateErr;

      await logAdminAction(
        getAdminEmail(),
        'subscription_status_sync',
        'company',
        mismatch.company.id,
        mismatch.company.name,
        {
          old_value: mismatch.company.subscription_status,
          new_value: newStatus,
          mismatch_type: mismatch.type,
          source: 'stripe_reconcile'
        }
      );

      await loadData();
    } catch (err) {
      alert('Sync failed: ' + err.message);
    } finally {
      setSyncing(prev => ({ ...prev, [key]: false }));
    }
  };

  const syncAll = async (mismatches) => {
    const confirmed = window.confirm(
      `Sync all ${mismatches.length} mismatches?\n\nThis will update each company's subscription_status to match Stripe's reality.`
    );
    if (!confirmed) return;

    setSyncing(prev => ({ ...prev, _all: true }));
    let successCount = 0;
    let failCount = 0;
    const adminEmail = getAdminEmail();

    for (const m of mismatches) {
      const newStatus = getTargetStatus(m);
      try {
        const { error: updateErr } = await supabase
          .from('companies')
          .update({ subscription_status: newStatus })
          .eq('id', m.company.id);

        if (updateErr) throw updateErr;

        await logAdminAction(
          adminEmail,
          'subscription_status_sync',
          'company',
          m.company.id,
          m.company.name,
          {
            old_value: m.company.subscription_status,
            new_value: newStatus,
            mismatch_type: m.type,
            source: 'stripe_reconcile_batch'
          }
        );
        successCount++;
      } catch {
        failCount++;
      }
    }

    setSyncing(prev => ({ ...prev, _all: false }));
    alert(`Sync complete: ${successCount} updated, ${failCount} failed`);
    await loadData();
  };

  useEffect(() => {
    loadData();
  }, []);

  // Build reconciliation analysis
  const analyze = () => {
    if (!stripeData || !dbCompanies.length) return null;

    const stripeSubs = stripeData.subscriptions || [];

    // Group Stripe subs by status
    const stripeByStatus = {};
    stripeSubs.forEach(sub => {
      stripeByStatus[sub.status] = (stripeByStatus[sub.status] || 0) + 1;
    });

    // Map Stripe subs by company_id for matching
    const stripeByCompanyId = {};
    const stripeByCustomerId = {};
    stripeSubs.forEach(sub => {
      if (sub.company_id) stripeByCompanyId[sub.company_id] = sub;
      if (sub.customer_id) stripeByCustomerId[sub.customer_id] = sub;
    });

    // Find mismatches
    const mismatches = [];

    // Companies in DB with stripe_customer_id but no matching Stripe sub
    dbCompanies.forEach(company => {
      if (company.stripe_customer_id) {
        const stripeSub = stripeByCustomerId[company.stripe_customer_id];
        if (!stripeSub) {
          mismatches.push({
            type: 'db_has_stripe_id_no_sub',
            severity: 'warning',
            company,
            detail: `DB has stripe_customer_id (${company.stripe_customer_id}) but no active subscription in Stripe`
          });
        } else if (stripeSub.status !== company.subscription_status) {
          // Status mismatch
          mismatches.push({
            type: 'status_mismatch',
            severity: stripeSub.status === 'active' && company.subscription_status !== 'active' ? 'error' : 'warning',
            company,
            stripeSub,
            detail: `Stripe: ${stripeSub.status} vs DB: ${company.subscription_status}`
          });
        }
      }
    });

    // Companies with no stripe_customer_id at all (never went through checkout)
    const noStripe = dbCompanies.filter(c => !c.stripe_customer_id);

    // Stripe subs with no matching company (orphaned)
    const orphanedSubs = stripeSubs.filter(sub => {
      if (sub.company_id) {
        return !dbCompanies.find(c => c.id === sub.company_id);
      }
      return !dbCompanies.find(c => c.stripe_customer_id === sub.customer_id);
    });

    // Active in Stripe (paying)
    const activeInStripe = stripeSubs.filter(s => s.status === 'active' || s.status === 'trialing');

    // Active in DB
    const activeInDb = dbCompanies.filter(c => c.subscription_status === 'active' || c.subscription_status === 'trialing');

    return {
      stripeByStatus,
      mismatches,
      noStripe,
      orphanedSubs,
      activeInStripe,
      activeInDb,
      totalStripeSubs: stripeSubs.length,
      totalDbCompanies: dbCompanies.length,
      stripeSubs
    };
  };

  const analysis = analyze();

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
    return d.toLocaleDateString();
  };

  const formatMoney = (cents) => {
    if (!cents) return '$0';
    return '$' + (cents / 100).toFixed(2);
  };

  const statusColor = (status) => {
    switch (status) {
      case 'active': return '#4ecca3';
      case 'trialing': return '#f39c12';
      case 'past_due': return '#e74c3c';
      case 'canceled': case 'inactive': return '#888';
      default: return '#888';
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#e0e0e0', marginBottom: '0.25rem' }}>
            Stripe Reconciliation
          </h1>
          <p style={{ color: '#888', fontSize: '0.85rem' }}>
            Compare Stripe subscriptions with internal database
            {lastFetched && <span> &middot; Last fetched: {lastFetched.toLocaleTimeString()}</span>}
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.75rem 1.25rem', background: '#ff6b35', color: '#fff',
            border: 'none', borderRadius: '8px', fontWeight: '600', cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1
          }}
        >
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#2a1a1a', border: '1px solid #e74c3c', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem', color: '#e74c3c' }}>
          {error}
        </div>
      )}

      {analysis && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <SummaryCard
              label="Stripe Subscriptions"
              value={analysis.totalStripeSubs}
              icon={CreditCard}
              color="#3498db"
              detail={Object.entries(analysis.stripeByStatus).map(([k, v]) => `${v} ${k}`).join(', ')}
            />
            <SummaryCard
              label="DB Companies"
              value={analysis.totalDbCompanies}
              icon={Building2}
              color="#9b59b6"
              detail={`${analysis.activeInDb.length} active/trialing`}
            />
            <SummaryCard
              label="Active in Stripe"
              value={analysis.activeInStripe.length}
              icon={CheckCircle}
              color="#4ecca3"
              detail="active + trialing"
            />
            <SummaryCard
              label="No Stripe (Free)"
              value={analysis.noStripe.length}
              icon={AlertTriangle}
              color={analysis.noStripe.length > 0 ? '#e74c3c' : '#4ecca3'}
              detail="companies with no stripe_customer_id"
            />
          </div>

          {/* Mismatches */}
          {analysis.mismatches.length > 0 && (
            <Section title="Status Mismatches" count={analysis.mismatches.length} color="#f39c12">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                <button
                  onClick={() => syncAll(analysis.mismatches)}
                  disabled={syncing._all}
                  style={{
                    padding: '0.5rem 1rem', background: '#f39c12', color: '#000',
                    border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '0.8rem',
                    cursor: syncing._all ? 'wait' : 'pointer', opacity: syncing._all ? 0.7 : 1
                  }}
                >
                  {syncing._all ? 'Syncing...' : `Sync All (${analysis.mismatches.length})`}
                </button>
              </div>
              {analysis.mismatches.map((m, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem',
                  background: '#1e1e1e', borderRadius: '6px', marginBottom: '0.5rem',
                  borderLeft: `3px solid ${m.severity === 'error' ? '#e74c3c' : '#f39c12'}`
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#e0e0e0', fontWeight: '600' }}>{m.company.name}</div>
                    <div style={{ color: '#888', fontSize: '0.8rem' }}>{m.detail}</div>
                  </div>
                  <div style={{ color: '#666', fontSize: '0.75rem', fontFamily: 'monospace', marginRight: '0.5rem' }}>
                    {m.company.id.slice(0, 8)}
                  </div>
                  <button
                    onClick={() => syncOne(m)}
                    disabled={syncing[m.company.id] || syncing._all}
                    style={{
                      padding: '0.4rem 0.75rem', background: '#ff6b35', color: '#fff',
                      border: 'none', borderRadius: '5px', fontWeight: '600', fontSize: '0.75rem',
                      cursor: (syncing[m.company.id] || syncing._all) ? 'wait' : 'pointer',
                      opacity: (syncing[m.company.id] || syncing._all) ? 0.7 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {syncing[m.company.id] ? 'Syncing...' : 'Sync Status'}
                  </button>
                </div>
              ))}
            </Section>
          )}

          {/* Companies with No Stripe */}
          {analysis.noStripe.length > 0 && (
            <Section title="Companies Without Stripe" count={analysis.noStripe.length} color="#e74c3c">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                      <th style={thStyle}>Company</th>
                      <th style={thStyle}>Tier</th>
                      <th style={thStyle}>DB Status</th>
                      <th style={thStyle}>Created</th>
                      <th style={thStyle}>Trial Ends</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.noStripe.map(c => (
                      <tr key={c.id} style={{ borderBottom: '1px solid #1e1e1e' }}>
                        <td style={tdStyle}>
                          <div style={{ color: '#e0e0e0', fontWeight: '600' }}>{c.name}</div>
                          <div style={{ color: '#666', fontSize: '0.7rem', fontFamily: 'monospace' }}>{c.id.slice(0, 8)}</div>
                        </td>
                        <td style={tdStyle}><span style={{ color: '#888' }}>{c.tier || '—'}</span></td>
                        <td style={tdStyle}>
                          <span style={{ color: statusColor(c.subscription_status), fontWeight: '600' }}>
                            {c.subscription_status || '—'}
                          </span>
                        </td>
                        <td style={tdStyle}><span style={{ color: '#888', fontSize: '0.85rem' }}>{formatDate(c.created_at)}</span></td>
                        <td style={tdStyle}><span style={{ color: '#888', fontSize: '0.85rem' }}>{formatDate(c.trial_ends_at)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Orphaned Stripe Subs */}
          {analysis.orphanedSubs.length > 0 && (
            <Section title="Orphaned Stripe Subscriptions" count={analysis.orphanedSubs.length} color="#f39c12">
              <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                Subscriptions in Stripe with no matching company in the database
              </p>
              {analysis.orphanedSubs.map((sub, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem',
                  background: '#1e1e1e', borderRadius: '6px', marginBottom: '0.5rem'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#e0e0e0', fontWeight: '600' }}>{sub.customer_email || sub.customer_name || 'Unknown'}</div>
                    <div style={{ color: '#888', fontSize: '0.8rem' }}>
                      {sub.status} &middot; {formatMoney(sub.amount)}/{sub.interval} &middot; {sub.customer_id}
                    </div>
                  </div>
                </div>
              ))}
            </Section>
          )}

          {/* All Stripe Subscriptions */}
          <Section title="All Stripe Subscriptions" count={analysis.totalStripeSubs} color="#3498db" defaultOpen={false}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                    <th style={thStyle}>Customer</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Tier</th>
                    <th style={thStyle}>Company ID</th>
                    <th style={thStyle}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.stripeSubs.map((sub, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #1e1e1e' }}>
                      <td style={tdStyle}>
                        <div style={{ color: '#e0e0e0' }}>{sub.customer_email || sub.customer_name || '—'}</div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ color: statusColor(sub.status), fontWeight: '600' }}>{sub.status}</span>
                      </td>
                      <td style={tdStyle}><span style={{ color: '#888' }}>{formatMoney(sub.amount)}/{sub.interval || '?'}</span></td>
                      <td style={tdStyle}><span style={{ color: '#888' }}>{sub.tier || '—'}</span></td>
                      <td style={tdStyle}><span style={{ color: '#666', fontSize: '0.75rem', fontFamily: 'monospace' }}>{sub.company_id ? sub.company_id.slice(0, 8) : '—'}</span></td>
                      <td style={tdStyle}><span style={{ color: '#888', fontSize: '0.85rem' }}>{formatDate(sub.created)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </>
      )}

      {!analysis && !loading && !error && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
          Click Refresh to load data
        </div>
      )}
    </div>
  );
}

// Helper components
function SummaryCard({ label, value, icon: Icon, color, detail }) {
  return (
    <div style={{
      background: '#1a1a1a', borderRadius: '10px', padding: '1.25rem',
      border: '1px solid #2a2a2a', borderLeft: `4px solid ${color}`
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <Icon size={18} color={color} />
        <span style={{ color: '#888', fontSize: '0.8rem' }}>{label}</span>
      </div>
      <div style={{ fontSize: '2rem', fontWeight: '700', color }}>{value}</div>
      {detail && <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem' }}>{detail}</div>}
    </div>
  );
}

function Section({ title, count, color, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      background: '#1a1a1a', borderRadius: '12px', border: '1px solid #2a2a2a',
      marginBottom: '1.5rem', overflow: 'hidden'
    }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem', cursor: 'pointer', userSelect: 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h3 style={{ color: '#e0e0e0', fontSize: '1rem', margin: 0 }}>{title}</h3>
          <span style={{
            background: `${color}20`, color, fontSize: '0.75rem',
            padding: '0.2rem 0.5rem', borderRadius: '4px', fontWeight: '600'
          }}>
            {count}
          </span>
        </div>
        <ArrowRight size={16} color="#888" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: '0.2s' }} />
      </div>
      {open && <div style={{ padding: '0 1.25rem 1.25rem' }}>{children}</div>}
    </div>
  );
}

const thStyle = { textAlign: 'left', padding: '0.75rem', color: '#888', fontWeight: '500', fontSize: '0.8rem' };
const tdStyle = { padding: '0.75rem' };
